import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import { Pill, PriorityBadge, SourceBadge, StatusBadge } from '@/components/base/Badges';
import { driversList } from '@/mocks/drivers';
import Toast, { type ToastType } from '@/components/base/Toast';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useAppData } from '@/store/AppDataContext';
import { useState, useEffect } from 'react';
import type { OrderStatus } from '@/mocks/orders';

const dotClass: Record<OrderStatus, string> = {
  pending: 'bg-warning-500',
  accepted: 'bg-info-500',
  processing: 'bg-secondary-500',
  ready: 'bg-success-500',
  completed: 'bg-success-600',
  cancelled: 'bg-danger-500',
  rejected: 'bg-danger-500',
  needs_driver: 'bg-accent-500',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-3 font-semibold text-[13px] text-foreground-500">{title}</h4>
      {children}
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-foreground-500">{label}</span>
      <span className="text-[13px] font-medium text-foreground-900">{value}</span>
    </div>
  );
}

interface ToastState { type: ToastType; message: string }

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orders, patchOrderStatus, assignOrderDriver } = useAppData();
  const order = orders.find((o) => o.id === id);

  const [toast, setToast] = useState<ToastState | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    if (!order) navigate('/orders', { replace: true });
  }, [order, navigate]);

  if (!order) return null;

  const driver = order.driverId ? driversList.find((d) => d.id === order.driverId) : undefined;

  const showToast = (type: ToastType, message: string) => setToast({ type, message });

  const handleAction = (action: string) => {
    switch (action) {
      case 'accept':
        patchOrderStatus(order.id, 'accepted', 'تم قبول الطلب');
        showToast('success', 'تم قبول الطلب وبدأ التجهيز للاستلام');
        break;
      case 'start':
        patchOrderStatus(order.id, 'processing', 'بدأ التنفيذ في المغسلة');
        showToast('info', 'تم تحديث الطلب إلى قيد التنفيذ');
        break;
      case 'ready':
        patchOrderStatus(order.id, 'ready', 'جاهز للتسليم');
        showToast('success', 'تم تعليم الطلب كجاهز للتسليم');
        break;
      case 'complete':
        patchOrderStatus(order.id, 'completed', 'تم تسليم الطلب للعميل');
        showToast('success', 'تم تأكيد تسليم الطلب بنجاح');
        break;
      case 'reject':
        setRejectOpen(true);
        break;
      case 'assign':
        navigate(`/orders/assign?orderId=${order.id}&orderNo=${encodeURIComponent(order.orderNo)}`);
        break;
      default:
        break;
    }
  };

  const confirmReject = () => {
    patchOrderStatus(order.id, 'rejected', 'تم رفض الطلب وإعادة المبلغ');
    setRejectOpen(false);
    showToast('info', 'تم رفض الطلب وإعادة المبلغ للعميل');
  };

  const copyPhone = (phone: string) => {
    if (navigator.clipboard) navigator.clipboard.writeText(phone).catch(() => undefined);
    showToast('info', 'تم نسخ رقم الهاتف');
  };

  const renderActions = () => {
    if (order.status === 'pending') {
      return (
        <div className="flex gap-3">
          <Button variant="danger" size="lg" className="flex-1" onClick={() => handleAction('reject')}>رفض الطلب</Button>
          <Button variant="cta" size="lg" className="flex-1" icon="ri-check-line" onClick={() => handleAction('accept')}>قبول الطلب</Button>
        </div>
      );
    }
    if (order.status === 'accepted') {
      return <Button variant="primary" size="lg" fullWidth icon="ri-play-line" onClick={() => handleAction('start')}>بدء التنفيذ</Button>;
    }
    if (order.status === 'processing') {
      return <Button variant="primary" size="lg" fullWidth icon="ri-checkbox-circle-line" onClick={() => handleAction('ready')}>تعليم جاهز للتسليم</Button>;
    }
    if (order.status === 'needs_driver') {
      return <Button variant="cta" size="lg" fullWidth icon="ri-e-bike-2-line" onClick={() => handleAction('assign')}>تعيين مندوب</Button>;
    }
    if (order.status === 'ready') {
      return <Button variant="soft" size="lg" fullWidth icon="ri-hand-heart-line" onClick={() => handleAction('complete')}>تأكيد تسليم الطلب</Button>;
    }
    return null;
  };

  return (
    <DashboardLayout title={`طلب ${order.orderNo}`}>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="mx-auto w-full max-w-[720px]">
        <PageHeader
          title={`طلب ${order.orderNo}`}
          backTo="/orders"
          actions={<StatusBadge status={order.status} />}
        />

        <div className="space-y-4">
          <Section title="بيانات العميل">
            <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
              <p className="font-semibold text-[14px] text-foreground-950">{order.customer}</p>
              <button
                type="button"
                onClick={() => copyPhone(order.phone)}
                className="mt-1.5 flex items-center gap-2 text-[13px] text-secondary-600 transition-colors hover:text-secondary-700"
              >
                <i className="ri-phone-line text-base" aria-hidden="true"></i>
                <span dir="ltr">{order.phone}</span>
                <i className="ri-file-copy-line text-sm text-foreground-400" aria-hidden="true"></i>
              </button>
              {order.deliveryMethod !== 'استلام فقط' && (
                <p className="mt-2 flex items-start gap-2 text-[12px] leading-relaxed text-foreground-500">
                  <i className="ri-map-pin-line mt-0.5 text-base text-foreground-400" aria-hidden="true"></i>
                  {order.district} — {order.address}
                </p>
              )}
            </div>
          </Section>

          <Section title="القطع المطلوبة">
            <div className="overflow-hidden rounded-card border border-background-200 bg-white shadow-card">
              {order.items.map((item, index) => (
                <div
                  key={item.id}
                  className={['flex items-center justify-between gap-3 px-4 py-3', index > 0 ? 'border-t border-background-100' : ''].join(' ')}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-foreground-900">{item.name}</p>
                    <p className="mt-0.5 text-[12px] text-foreground-500">{item.service} • الكمية {item.quantity}</p>
                  </div>
                  <span className="whitespace-nowrap text-[13px] font-semibold text-foreground-950">
                    {item.priceEach * item.quantity} ر.س
                  </span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="ملخص الطلب">
            <div className="rounded-card border border-background-200 bg-white p-2 shadow-card">
              <div className="flex items-center justify-between px-2 py-2">
                <span className="text-[13px] text-foreground-500">الأولوية</span>
                <PriorityBadge priority={order.priority} />
              </div>
              <div className="flex items-center justify-between border-t border-background-100 px-2 py-2">
                <span className="text-[13px] text-foreground-500">طريقة التوصيل</span>
                <span className="text-[13px] font-medium text-foreground-900">{order.deliveryMethod}</span>
              </div>
              <div className="flex items-center justify-between border-t border-background-100 px-2 py-2">
                <span className="text-[13px] text-foreground-500">المصدر</span>
                <SourceBadge source={order.source} />
              </div>
              {order.coupon && (
                <div className="flex items-center justify-between border-t border-background-100 px-2 py-2">
                  <span className="text-[13px] text-foreground-500">كوبون</span>
                  <Pill tone="success" icon="ri-coupon-3-line">{order.coupon.code} — خصم {order.coupon.value} ر.س</Pill>
                </div>
              )}
            </div>
          </Section>

          <Section title="تفاصيل الدفع">
            <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
              <InfoRow label="المجموع الجزئي" value={`${order.subtotal} ر.س`} />
              <InfoRow label="رسوم التوصيل" value={`${order.deliveryFees} ر.س`} />
              <InfoRow label="الخصم" value={order.discount > 0 ? `- ${order.discount} ر.س` : '—'} />
              <div className="mt-2 flex items-center justify-between border-t border-background-200 pt-3">
                <span className="font-semibold text-[14px] text-foreground-950">الإجمالي</span>
                <span className="font-bold text-[18px] text-primary-600">{order.total} ر.س</span>
              </div>
            </div>
          </Section>

          <Section title="سجل الحالات">
            <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
              <ol className="relative space-y-4 border-e border-dashed border-background-300 pe-4">
                {order.history.map((event, index) => (
                  <li key={`${event.status}-${index}`} className="relative">
                    <span className={['absolute -right-[22px] top-1 h-3 w-3 rounded-full ring-2 ring-white', dotClass[event.status]].join(' ')} />
                    <p className="text-[13px] font-medium text-foreground-900">{event.label}</p>
                    <p className="mt-0.5 text-[12px] text-foreground-400">{event.date} • {event.time}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Section>

          {driver && (
            <Section title="بيانات المندوب">
              <div className="flex items-center gap-3 rounded-card border border-background-200 bg-white p-4 shadow-card">
                <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-background-200">
                  <img src={driver.photo} alt={driver.name} className="h-full w-full object-cover object-top" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-foreground-950">{driver.name}</p>
                  <p className="mt-0.5 text-[12px] text-foreground-500">تابع • {driver.vehicle}</p>
                </div>
                <a
                  href={`tel:${driver.phone}`}
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-600 transition-colors hover:bg-secondary-200"
                  aria-label="اتصال بالمندوب"
                >
                  <i className="ri-phone-line text-base" aria-hidden="true"></i>
                </a>
              </div>
            </Section>
          )}

          {renderActions() && (
            <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
              {renderActions()}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={rejectOpen}
        title="تأكيد رفض الطلب"
        message="هل أنت متأكد من رفض الطلب؟ سيتم إعادة المبلغ للعميل ولا يمكن التراجع عن هذه الخطوة."
        confirmLabel="تأكيد الرفض"
        tone="danger"
        onConfirm={confirmReject}
        onClose={() => setRejectOpen(false)}
      />
    </DashboardLayout>
  );
}